import { useEffect, useMemo, useState } from "react";
import { useRecoilState, useRecoilValue } from "recoil";

import {
  noSpaceIdSelector,
  spaceTypesSelector,
} from "../../../recoil/spaces/inProgressSpaceListState";
import { taskPopupState } from "../../../recoil/taskDetail/taskPopupState";

import PropertyTitle from "../Common/PropertyTitle";

import styles from "./SpaceModal.module.css";
import { SpaceKind, SpaceShortcut } from "../../../types/space/enum";
import { SpaceIdType } from "../../../types/space";
import { BlockPopupState } from "../../../types/block/BlockType";
import { BlockType } from "../../../types/block/type";
import SpaceDropdown from "../../../compoenets/organism/TaskDetail/SpaceDropdown";
import ProjectIcon from "../../../compoenets/atom/icons/ProjectIcon";
import { juneTrack } from "../../../utils/june/analytics";
import { JUNE_EVENT } from "../../../hooks/june/juneEvent";

interface SpaceModalProps {
  expand: boolean;
  projectDropdownRef: React.RefObject<HTMLDivElement>;
  titleRef: React.RefObject<HTMLTextAreaElement> | null;
  handleChangeLocalTitle: (newLocalTitle: string) => void;
  isInputQuickSpaceShortcut: boolean;
  setIsInputQuickSpaceShortcut: React.Dispatch<React.SetStateAction<boolean>>;
}
export default function SpaceModal({
  expand,
  projectDropdownRef,
  titleRef,
  handleChangeLocalTitle,
  isInputQuickSpaceShortcut,
  setIsInputQuickSpaceShortcut,
}: SpaceModalProps) {
  // Recoil states
  const [taskDetail, setTaskDetail] = useRecoilState<BlockPopupState>(taskPopupState);
  const inProgressSpaceList = useRecoilValue(spaceTypesSelector([SpaceKind.Normal]));
  const noSpaceId = useRecoilValue(noSpaceIdSelector);

  // React states
  const [isSpaceDropdownOpen, setIsSpaceDropdownOpen] = useState(isInputQuickSpaceShortcut);

  // Memoized values
  const selectedSpaceId = useMemo(
    () =>
      taskDetail.data?.spaceId && taskDetail.data?.spaceId !== noSpaceId
        ? String(taskDetail.data?.spaceId)
        : undefined,
    [taskDetail, noSpaceId]
  );

  useEffect(
    function handleInputQuickSpaceShortcut() {
      if (!isInputQuickSpaceShortcut) return;
      setIsSpaceDropdownOpen(true);
    },
    [isInputQuickSpaceShortcut]
  );

  const handleSelectProject = (spaceId: SpaceIdType) => {
    updateTaskConfig("spaceId", spaceId);
    setIsSpaceDropdownOpen(false);

    // QuickSpaceShortcut으로 dropdown을 연 경우, 타이틀에 # 없애주기
    const currentTitle = titleRef?.current?.value ?? "";
    const endsWithQuickSpaceShortcut = currentTitle.endsWith(SpaceShortcut.QuickSpace);
    if (isInputQuickSpaceShortcut && endsWithQuickSpaceShortcut) {
      handleChangeLocalTitle(currentTitle.slice(0, -1));

      // quickSpace Shortcut으로 space 추가한 경우 june 이벤트 전송
      juneTrack(JUNE_EVENT.USE_QUICK_SPACE, {
        location: taskDetail?.type,
      });
    }
  };

  const updateTaskConfig = (key: keyof BlockType, value: number) => {
    setTaskDetail((prevState) => {
      // NOTE 실제로 data 가 null인 경우는 없으나 예외처리
      if (prevState.data === null) {
        return { ...prevState, data: null };
      }

      // data가 존재하는 경우 업데이트
      return {
        ...prevState,
        data: {
          ...prevState.data,
          [key]: value,
        },
      };
    });
  };

  const handleDeleteProject = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (!noSpaceId) return;
    updateTaskConfig("spaceId", noSpaceId);
  };

  const handleEscapeKeyDown = () => {
    // QuickSpaceShortcut으로 dropdown을 연 경우, esc 키 눌렀을 때 title에 포커스
    if (isInputQuickSpaceShortcut) {
      titleRef?.current?.focus();
    }
  };

  const handleFocusCloseDropdown = () => {
    // dropdown이 닫힐 때 isInputQuickSpaceShortcut 초기화
    setIsInputQuickSpaceShortcut(false);
  };

  return (
    <>
      <div className={styles["setting-property-btn"]}>
        <PropertyTitle
          Icon={<ProjectIcon />}
          label="Projects"
          expand={expand}
        />

        <SpaceDropdown
          spaceList={inProgressSpaceList}
          open={isSpaceDropdownOpen}
          onOpenChange={setIsSpaceDropdownOpen}
          selectedValue={selectedSpaceId}
          onSelect={handleSelectProject}
          onFocusClose={handleFocusCloseDropdown}
          onKeydownEscape={handleEscapeKeyDown}
          handleDeleteProject={handleDeleteProject}
          projectDropdownRef={projectDropdownRef}
        />
      </div>
    </>
  );
}
